import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableCompData, TabsComp } from "../../../../components";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import InvestorPortalPageHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import InvestorPortalTableSection from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import {
  RequestStatusBadge,
  KycStatusBadge,
  ParticipantTypeBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import { INVESTMENT_REQUEST_STATUS } from "../../../../store/admin/investorPortal/constants";
import { formatMoney } from "../../../../utils/currencyFormat";
import { useListStaffInvestmentRequestsQuery } from "../../../../store/admin/investorPortal/staffInvestmentsApi";
import { mapStaffInvestmentRequestListItem } from "../../../../utils/staffInvestmentsAdapter";
import moment from "moment";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 10;

function RequestsTable({ statusFilter }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const apiQuery = useListStaffInvestmentRequestsQuery({
    page,
    per_page: PER_PAGE,
    status: statusFilter || undefined,
  });

  const pageData = useMemo(
    () => (apiQuery.data?.items || []).map(mapStaffInvestmentRequestListItem).filter(Boolean),
    [apiQuery.data]
  );

  const apiPagination = apiQuery.data?.pagination || {};
  const pagination = {
    currentPage: apiPagination.current_page || page,
    totalPages: apiPagination.last_page || 1,
    total: apiPagination.total || pageData.length,
    perPage: apiPagination.per_page || PER_PAGE,
    onPageChange: setPage,
  };

  const columns = [
    { name: "Request ID", selector: (r) => r.id, minWidth: "100px" },
    { name: "Participant", selector: (r) => r.participantName, minWidth: "150px", grow: 1 },
    { name: "Type", cell: (r) => <ParticipantTypeBadge type={r.participantType} />, minWidth: "150px" },
    { name: "Opportunity", selector: (r) => r.opportunityTitle, minWidth: "170px", grow: 1 },
    { name: "Units", selector: (r) => r.units, minWidth: "70px", center: true },
    { name: "Total", selector: (r) => formatMoney(r.totalNgn), minWidth: "130px", right: true },
    { name: "KYC", cell: (r) => <KycStatusBadge status={r.kycStatus} />, minWidth: "100px" },
    { name: "Status", cell: (r) => <RequestStatusBadge status={r.status} />, minWidth: "120px" },
    { name: "Submitted", selector: (r) => moment(r.submittedAt).format("DD-MMM-YY"), minWidth: "105px" },
    { name: "Reviewed by", selector: (r) => r.reviewedByName || "—", minWidth: "120px" },
    {
      name: "Actions",
      cell: (r) => (
        <TableActionDropdown
          items={[
            {
              name: "View details",
              action: () =>
                navigate(
                  `/super_admin/investor_portal/investments/requests/${encodeURIComponent(r.reference || r.id)}`
                ),
            },
          ]}
          row={r}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  const emptyState = apiQuery.isError ? (
    <InvestorPortalTableEmpty
      variant="error"
      title="We couldn’t load investment requests."
      onCta={() => apiQuery.refetch()}
    />
  ) : statusFilter ? (
    <InvestorPortalTableEmpty
      variant="filtered"
      title="No requests match this queue."
      description="Try another status tab or clear the filter by viewing All."
      ctaLabel={undefined}
    />
  ) : (
    <InvestorPortalTableEmpty
      variant="empty"
      title="No investment requests yet."
      description="Requests appear here when investors submit funding applications."
    />
  );

  return (
    <InvestorPortalTableSection pagination={pagination} data-tour="staff-invest-req-queue">
      <TableCompData
        columns={columns}
        data={pageData}
        progressPending={apiQuery.isFetching}
        progressComponent={<TableSkeletonLoader rows={8} columns={10} />}
        noDataComponent={emptyState}
      />
    </InvestorPortalTableSection>
  );
}

function InvestmentRequestsPage() {
  const useTab = [
    { name: "All", component: <RequestsTable statusFilter="" /> },
    {
      name: "Pending payment",
      component: <RequestsTable statusFilter={INVESTMENT_REQUEST_STATUS.PENDING_PAYMENT} />,
    },
    { name: "Allocated", component: <RequestsTable statusFilter={INVESTMENT_REQUEST_STATUS.ALLOCATED} /> },
    { name: "Submitted", component: <RequestsTable statusFilter={INVESTMENT_REQUEST_STATUS.SUBMITTED} /> },
    { name: "Under Review", component: <RequestsTable statusFilter={INVESTMENT_REQUEST_STATUS.UNDER_REVIEW} /> },
    { name: "Approved", component: <RequestsTable statusFilter={INVESTMENT_REQUEST_STATUS.APPROVED} /> },
    { name: "Declined", component: <RequestsTable statusFilter={INVESTMENT_REQUEST_STATUS.DECLINED} /> },
    { name: "Matured", component: <RequestsTable statusFilter={INVESTMENT_REQUEST_STATUS.MATURED} /> },
    { name: "Cancelled", component: <RequestsTable statusFilter={INVESTMENT_REQUEST_STATUS.CANCELLED} /> },
  ];

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader title="Investment Requests" />

      <div className="mb-3" data-tour="staff-invest-req-tabs">
        <TabsComp
          TabArray={useTab}
          input="name"
          justifyContent="justify-content-start"
          tabClassName="px-0"
          TabArrayClassName="px-1"
        />
      </div>
    </div>
  );
}

export default withInvestorPortalGuard(InvestmentRequestsPage, "investment");
